import { Controller } from '@hotwired/stimulus';
import { subscribeTo } from '../channels/app_design_chat_channel';

export default class extends Controller {
  static targets = ['input', 'previewIframe', 'publishedIframe', 'fileInput'];
  static values = { conversationId: Number };

  connect() {
    if (this.conversationIdValue) {
      subscribeTo(this.conversationIdValue);
    }

    const chatMessages = document.getElementById('design-chat-messages');
    if (chatMessages) {
      chatMessages.scrollTop = chatMessages.scrollHeight;
    }

    this._statusHandler = (event) => this.handleStatus(event.detail);
    this._messageHandler = (event) => this.handleNewMessage(event.detail);
    document.addEventListener('app-design-chat:status', this._statusHandler);
    document.addEventListener('app-design-chat:message', this._messageHandler);
  }

  disconnect() {
    if (this._statusHandler) {
      document.removeEventListener('app-design-chat:status', this._statusHandler);
    }
    if (this._messageHandler) {
      document.removeEventListener('app-design-chat:message', this._messageHandler);
    }
    this.stopPolling();
  }

  handleKeydown(event) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.submitMessage();
    }
  }

  submitMessage() {
    const input = this.inputTarget;
    const message = input.value.trim();
    if (!message) return;

    input.value = '';
    input.disabled = true;

    const file = this._pendingFile || null;
    this._pendingFile = null;
    this.hideFilePreview();

    this.appendUserMessage(message + (file ? ` [image: ${file.name}]` : ''));
    this.showDesigning(true);

    if (this.conversationIdValue) {
      this.sendFollowUp(message, file);
    } else {
      this.createConversation(message, file);
    }
  }

  fileSelected(event) {
    const file = event.target.files[0];
    if (!file) return;
    this._pendingFile = file;

    const preview = document.getElementById('file-preview');
    const img = document.getElementById('file-preview-img');
    const name = document.getElementById('file-preview-name');
    if (preview && img && name) {
      img.src = URL.createObjectURL(file);
      name.textContent = file.name;
      preview.style.display = 'flex';
    }
  }

  removeFile(event) {
    event.preventDefault();
    this._pendingFile = null;
    this.hideFilePreview();
    if (this.hasFileInputTarget) this.fileInputTarget.value = '';
  }

  hideFilePreview() {
    const preview = document.getElementById('file-preview');
    if (preview) preview.style.display = 'none';
  }

  buildFormData(message, file) {
    const formData = new FormData();
    formData.append('message', message);
    if (file) formData.append('image', file);
    return formData;
  }

  createConversation(message, file) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    const body = this.buildFormData(message, file);

    fetch('/mobile_setups/chat', {
      method: 'POST',
      headers: {
        'X-CSRF-Token': csrfToken,
        'Accept': 'application/json'
      },
      body: body
    })
    .then(response => {
      if (!response.ok) {
        return response.text().then(text => {
          try {
            const json = JSON.parse(text);
            throw new Error(json.error || `Server error (${response.status})`);
          } catch (e) {
            if (e.message.includes('Server error') || e.message.includes('Failed')) throw e;
            throw new Error(`Server error (${response.status})`);
          }
        });
      }
      return response.json();
    })
    .then(data => {
      if (data.error) {
        this.showError(data.error);
        return;
      }
      this.conversationIdValue = data.conversation_id;
      subscribeTo(data.conversation_id);

      const emptyState = document.getElementById('design-empty-state');
      if (emptyState) emptyState.remove();

      this.startPolling();
    })
    .catch(error => {
      this.showError(error.message || 'Failed to start design. Please try again.');
      console.error('App design create error:', error);
    });
  }

  sendFollowUp(message, file) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    const body = this.buildFormData(message, file);

    fetch(`/mobile_setups/conversations/${this.conversationIdValue}/chat`, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': csrfToken,
        'Accept': 'application/json'
      },
      body: body
    })
    .then(response => {
      if (!response.ok) {
        return response.text().then(text => {
          try {
            const json = JSON.parse(text);
            throw new Error(json.error || `Server error (${response.status})`);
          } catch (e) {
            if (e.message.includes('Server error') || e.message.includes('Failed')) throw e;
            throw new Error(`Server error (${response.status})`);
          }
        });
      }
      return response.json();
    })
    .then(data => {
      if (data?.error) {
        this.showError(data.error);
      } else {
        this.startPolling();
      }
    })
    .catch(error => {
      this.showError(error.message || 'Failed to send message. Please try again.');
      console.error('App design ask error:', error);
    });
  }

  startPolling() {
    this.stopPolling();
    this._pollCount = 0;
    this._pollTimer = setInterval(() => {
      this._pollCount++;
      if (this._pollCount > 100) {
        this.stopPolling();
        this.showError('Design is taking longer than expected. Refresh the page to check for results.');
        return;
      }
      this.checkForResults();
    }, 3000);
  }

  stopPolling() {
    if (this._pollTimer) {
      clearInterval(this._pollTimer);
      this._pollTimer = null;
    }
  }

  checkForResults() {
    if (!this.conversationIdValue) return;

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    fetch(`/mobile_setups/conversations/${this.conversationIdValue}/status`, {
      headers: { 'Accept': 'application/json', 'X-CSRF-Token': csrfToken }
    })
    .then(r => r.json())
    .then(data => {
      if (data.ready) {
        this.stopPolling();
        this.onDesignComplete();
      } else if (data.step && data.step !== this._lastStep) {
        this._lastStep = data.step;
        this.updateProgressStep(data.step);
      }
    })
    .catch(() => {});
  }

  handleStatus(data) {
    if (data.status === 'designing' && data.step) {
      this.updateProgressStep(data.step);
    }

    if (data.status === 'done' || data.status === 'error') {
      this.showDesigning(false);
      this.stopPolling();
      this.inputTarget.disabled = false;
      this.inputTarget.focus();

      if (data.status === 'done') {
        this.onDesignComplete();
      }
    }
  }

  handleNewMessage(data) {
    this.showDesigning(false);
    this.stopPolling();
    this.inputTarget.disabled = false;
    this.onDesignComplete();
  }

  onDesignComplete() {
    // Reload the page to show updated chat messages and refreshed preview
    window.location.href = `/mobile_setups?id=${this.conversationIdValue}`;
  }

  useSuggestion(event) {
    event.preventDefault();
    const message = event.currentTarget.dataset.message;
    if (this.hasInputTarget) {
      this.inputTarget.value = message;
      this.submitMessage();
    }
  }

  selectConversation(event) {
    event.preventDefault();
    const id = event.currentTarget.dataset.conversationId;
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

    fetch(`/mobile_setups/restore/${id}`, {
      method: 'POST',
      headers: { 'X-CSRF-Token': csrfToken, 'Accept': 'text/html' }
    }).finally(() => {
      window.location.href = `/mobile_setups?id=${id}`;
    });
  }

  newConversation(event) {
    event.preventDefault();
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

    // Fire reset in background, redirect immediately
    fetch('/mobile_setups/reset', {
      method: 'POST',
      headers: { 'X-CSRF-Token': csrfToken, 'Accept': 'text/html' }
    });
    this.conversationIdValue = 0;
    window.location.href = '/mobile_setups';
  }

  appendUserMessage(content) {
    const chatMessages = document.getElementById('design-chat-messages');
    if (!chatMessages) return;

    const emptyState = document.getElementById('design-empty-state');
    if (emptyState) emptyState.remove();

    const html = `
      <div class="app-design-item app-design-item-user">
        <div class="app-design-item-header">
          <span class="app-design-role app-design-role-user">You</span>
          <span class="app-design-time">just now</span>
        </div>
        <div class="app-design-item-content"><p>${this.escapeHtml(content)}</p></div>
      </div>
    `;

    const indicator = document.getElementById('designing-indicator');
    if (indicator) {
      indicator.insertAdjacentHTML('beforebegin', html);
    } else {
      chatMessages.insertAdjacentHTML('beforeend', html);
    }

    chatMessages.scrollTop = chatMessages.scrollHeight;
  }

  updateProgressStep(step) {
    // Update the indicator text
    const stepText = document.getElementById('designing-step-text');
    if (stepText) {
      stepText.textContent = step;
    }

    // Also append as a conversational status line in the chat
    const chatMessages = document.getElementById('design-chat-messages');
    if (!chatMessages) return;

    // Remove previous step line (keep only the latest)
    const prevStep = document.getElementById('design-progress-step');
    if (prevStep) prevStep.remove();

    const html = `
      <div class="app-design-progress-line" id="design-progress-step">
        <span class="app-design-progress-dot"></span>
        <span>${this.escapeHtml(step)}</span>
      </div>
    `;
    const indicator = document.getElementById('designing-indicator');
    if (indicator) {
      indicator.insertAdjacentHTML('afterend', html);
    } else {
      chatMessages.insertAdjacentHTML('beforeend', html);
    }
    chatMessages.scrollTop = chatMessages.scrollHeight;
  }

  showDesigning(show) {
    const indicator = document.getElementById('designing-indicator');
    if (indicator) {
      indicator.style.display = show ? '' : 'none';
    }
    if (!show) {
      // Clean up progress lines when done
      const step = document.getElementById('design-progress-step');
      if (step) step.remove();
    }
    if (show) {
      this.updateProgressStep('Starting');
    }
    const chatMessages = document.getElementById('design-chat-messages');
    if (chatMessages) {
      chatMessages.scrollTop = chatMessages.scrollHeight;
    }
  }

  showError(message) {
    this.showDesigning(false);
    this.stopPolling();
    this.inputTarget.disabled = false;
    this.inputTarget.focus();

    const chatMessages = document.getElementById('design-chat-messages');
    if (!chatMessages) return;

    const html = `
      <div class="app-design-item app-design-item-assistant">
        <div class="app-design-item-header">
          <span class="app-design-role app-design-role-assistant">AI Designer</span>
          <span class="app-design-time">just now</span>
        </div>
        <div class="app-design-item-content" style="color: #a94442; font-style: italic;">
          <p>${this.escapeHtml(message)}</p>
        </div>
      </div>
    `;
    chatMessages.insertAdjacentHTML('beforeend', html);
    chatMessages.scrollTop = chatMessages.scrollHeight;
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}
